import { useCallback, useEffect, useState } from 'react';

export const LAYOUT_KEY = 'robotrons:layout';

// A phone, or a tablet held upright. The second clause is the tablet half: at
// 1100px it clears the 1024-wide iPad Pro in portrait while leaving every
// landscape desktop on the wide layout. Tune the number here, nowhere else.
export const MOBILE_QUERY = '(max-width: 767px), (max-width: 1100px) and (orientation: portrait)';

const PREFS = ['auto', 'mobile', 'desktop'];

function readPref() {
  try {
    const stored = localStorage.getItem(LAYOUT_KEY);
    if (PREFS.includes(stored)) return stored;
  } catch (e) {
    // Private mode / storage disabled — auto is a fine default.
  }
  return 'auto';
}

function queryList() {
  return typeof window.matchMedia === 'function' ? window.matchMedia(MOBILE_QUERY) : null;
}

/**
 * Which layout the game should draw — and the per-device preference behind it.
 *
 * The desktop and mobile trees used to be split by a CSS media query, which
 * left a tablet held upright on the cramped wide layout with no way out. The
 * breakpoint lives here now so a player can overrule it.
 */
export default function useLayoutMode() {
  const [pref, setPrefState] = useState(readPref);
  const [autoMobile, setAutoMobile] = useState(() => queryList()?.matches ?? false);

  useEffect(() => {
    const mql = queryList();
    if (!mql) return undefined;

    setAutoMobile(mql.matches);
    const onChange = e => setAutoMobile(e.matches);

    if (mql.addEventListener) {
      mql.addEventListener('change', onChange);
      return () => mql.removeEventListener('change', onChange);
    }
    // Safari < 14, which is most of the older iPads this exists for.
    mql.addListener(onChange);
    return () => mql.removeListener(onChange);
  }, []);

  const setPref = useCallback((next) => {
    const value = PREFS.includes(next) ? next : 'auto';
    setPrefState(value);
    try {
      localStorage.setItem(LAYOUT_KEY, value);
    } catch (e) {
      // Storage disabled — the choice still holds for this session.
    }
  }, []);

  const autoLayout = autoMobile ? 'mobile' : 'desktop';

  return {
    layout: pref === 'auto' ? autoLayout : pref,
    autoLayout,
    pref,
    setPref,
  };
}
